import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useStore from '../store/useStore';
import { FileText, Shield, RotateCcw, ChevronRight, Download, Globe } from 'lucide-react';

const LegalPage = () => {
  const { section } = useParams(); // 'privacy', 'terms', 'returns'
  const { language } = useStore();
  const [activeLang, setActiveLang] = useState(language === 'nl' ? 'nl' : 'fr');

  const sections = {
    privacy: {
      icon: Shield,
      title: { fr: 'Politique de confidentialité', en: 'Privacy Policy', nl: 'Privacybeleid' },
      lastUpdate: '29 janvier 2026'
    },
    terms: {
      icon: FileText,
      title: { fr: 'Conditions Générales de Vente', en: 'Terms and Conditions', nl: 'Algemene Verkoopvoorwaarden' },
      lastUpdate: '29 janvier 2026'
    },
    returns: {
      icon: RotateCcw,
      title: { fr: 'Politique de retour', en: 'Return Policy', nl: 'Retourbeleid' },
      lastUpdate: '29 janvier 2026'
    }
  };

  const currentSection = sections[section] || sections.privacy;
  const Icon = currentSection.icon;

  // Content for each section and language
  const content = {
    privacy: {
      fr: <PrivacyFR />,
      en: <PrivacyEN />,
      nl: <PrivacyNL />
    },
    terms: {
      fr: <TermsFR />,
      en: <TermsEN />,
      nl: <TermsNL />
    },
    returns: {
      fr: <ReturnsFR />,
      en: <ReturnsEN />,
      nl: <ReturnsNL />
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/shop" className="hover:text-amber-600">
              {language === 'fr' ? 'Accueil' : language === 'nl' ? 'Home' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{currentSection.title[activeLang]}</span>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Icon className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentSection.title[activeLang]}
                </h1>
                <p className="text-sm text-gray-500">
                  {activeLang === 'fr' ? 'Dernière mise à jour' : activeLang === 'nl' ? 'Laatst bijgewerkt' : 'Last updated'}: {currentSection.lastUpdate}
                </p>
              </div>
            </div>

            {/* Language Selector */}
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <div className="flex rounded-lg border overflow-hidden">
                {['fr', 'en', 'nl'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      activeLang === lang
                        ? 'bg-amber-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-6 border-t pt-4">
            {Object.entries(sections).map(([key, sec]) => {
              const TabIcon = sec.icon;
              return (
                <Link
                  key={key}
                  to={`/legal/${key}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    section === key
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {sec.title[activeLang]}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
          <div className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-a:text-amber-600">
            {content[section]?.[activeLang] || content.privacy[activeLang]}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-amber-50 rounded-xl p-6 mt-6 border border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-2">
            {activeLang === 'fr' ? 'Contact' : activeLang === 'nl' ? 'Contact' : 'Contact'}
          </h3>
          <p className="text-amber-800">
            EBB-Shop – Rue du Centre 182, 5030 Gembloux, Belgique<br />
            Email: <a href="mailto:admin@shop-ebb.com" className="underline">admin@shop-ebb.com</a><br />
            Tél: +32 470 00 00 00<br />
            TVA: BE0700 100 114
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== PRIVACY POLICY ==============

const PrivacyFR = () => (
  <div>
    <h2>1) Responsable du traitement</h2>
    <p>Le responsable du traitement est EBB‑Shop, Rue du Centre, 182 à 5030 Gembloux, TVA BE0700 100 114.</p>

    <h2>2) Données traitées</h2>
    <p>Selon votre utilisation du site, nous pouvons traiter :</p>
    <ul>
      <li><strong>Données d'identification et de contact :</strong> nom, prénom, société (si applicable), n° TVA (si applicable), e‑mail, téléphone, adresses de livraison et de facturation.</li>
      <li><strong>Données de commande :</strong> produits, quantités, prix, remises, historique d'achats, retours, échanges, SAV.</li>
      <li><strong>Données de paiement :</strong> statut de paiement, références/identifiants de transaction (nous ne stockons pas vos données de carte bancaire complètes).</li>
      <li><strong>Données techniques :</strong> adresse IP, identifiants/horodatages, logs de sécurité, données de navigation et cookies.</li>
    </ul>

    <h2>3) Finalités et bases légales</h2>
    <ul>
      <li>Créer et gérer votre compte et vous permettre d'utiliser le site : exécution du contrat / mesures précontractuelles.</li>
      <li>Traiter vos commandes (préparation, livraison, retours, service client) : exécution du contrat.</li>
      <li>Gérer la facturation et la comptabilité (y compris obligations TVA) : obligation légale.</li>
      <li>Prévenir la fraude et sécuriser le site : intérêt légitime.</li>
      <li>Répondre à vos demandes (contact, SAV) : intérêt légitime et/ou exécution du contrat.</li>
      <li>Marketing (newsletter / offres) : consentement lorsque requis, avec désinscription possible à tout moment.</li>
    </ul>

    <h2>4) Destinataires – sous‑traitants</h2>
    <ul>
      <li><strong>OVH</strong> (VPS / hébergement) : hébergement et exploitation technique du site.</li>
      <li><strong>Mollie</strong> (paiements) : traitement des paiements et gestion des statuts de transaction.</li>
      <li><strong>bpost</strong> (transport / livraison) : prise en charge de l'expédition, livraison, suivi, retours.</li>
      <li>Prestataires techniques nécessaires au fonctionnement et conseillers professionnels (comptable) si nécessaire.</li>
      <li>Autorités compétentes lorsque la loi l'exige.</li>
    </ul>

    <h2>5) Transferts hors EEE</h2>
    <p>Dans la mesure où un prestataire traiterait des données en dehors de l'Espace économique européen, nous mettons en place des garanties appropriées (ex. clauses contractuelles types).</p>

    <h2>6) Durées de conservation</h2>
    <ul>
      <li>Commandes, factures, données comptables : 7 à 10 ans (obligations légales).</li>
      <li>Compte client : tant que le compte est actif, puis suppression/archivage après 3 ans d'inactivité.</li>
      <li>SAV et demandes : le temps de traiter la demande puis archivage limité si nécessaire.</li>
      <li>Logs de sécurité : 12 mois.</li>
      <li>Marketing : jusqu'au retrait du consentement ou 3 ans après le dernier contact.</li>
    </ul>

    <h2>7) Cookies et traceurs</h2>
    <p>Nous utilisons des cookies strictement nécessaires au fonctionnement du site (panier, session, sécurité) et des cookies de mesure d'audience/marketing uniquement avec votre consentement.</p>

    <h2>8) Sécurité</h2>
    <p>Nous mettons en œuvre des mesures techniques et organisationnelles adaptées pour protéger vos données contre l'accès non autorisé, la perte ou l'altération.</p>

    <h2>9) Vos droits</h2>
    <p>Vous disposez des droits suivants : accès, rectification, effacement, limitation, opposition, portabilité, et retrait du consentement.</p>
    <p>Pour exercer vos droits : <a href="mailto:admin@shop-ebb.com">admin@shop-ebb.com</a></p>
    <p>Vous pouvez également introduire une réclamation auprès de l'Autorité de protection des données (Belgique) : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer">www.autoriteprotectiondonnees.be</a></p>
  </div>
);

const PrivacyEN = () => (
  <div>
    <h2>1) Data controller</h2>
    <p>The data controller is EBB‑Shop, Rue du Centre 182, 5030 Gembloux, VAT BE0700 100 114.</p>

    <h2>2) Personal data we process</h2>
    <p>Depending on how you use the website, we may process:</p>
    <ul>
      <li><strong>Identification & contact data:</strong> name, surname, company (if any), VAT number (if any), email, phone number, billing/shipping addresses.</li>
      <li><strong>Order data:</strong> items, quantities, prices, discounts, purchase history, returns, exchanges, customer support.</li>
      <li><strong>Payment data:</strong> payment status, transaction references/IDs (we do not store full card details).</li>
      <li><strong>Technical data:</strong> IP address, identifiers/timestamps, security logs, browsing data and cookies.</li>
    </ul>

    <h2>3) Purposes and legal bases</h2>
    <ul>
      <li>Create/manage your account and provide website features: performance of a contract / pre‑contractual steps.</li>
      <li>Process and fulfill orders: performance of a contract.</li>
      <li>Invoicing and accounting (including VAT obligations): legal obligation.</li>
      <li>Prevent fraud and secure the website: legitimate interests.</li>
      <li>Answer your requests: legitimate interests and/or performance of a contract.</li>
      <li>Marketing (newsletter/offers): consent when required; you can unsubscribe at any time.</li>
    </ul>

    <h2>4) Recipients – processors</h2>
    <ul>
      <li><strong>OVH</strong> (VPS hosting) for hosting and technical operations.</li>
      <li><strong>Mollie</strong> (payments) to process payments and transaction statuses.</li>
      <li><strong>bpost</strong> (shipping/delivery) for shipping, tracking and returns.</li>
      <li>Other technical providers and professional advisors when necessary.</li>
      <li>Public authorities when required by law.</li>
    </ul>

    <h2>5) International transfers</h2>
    <p>If any provider processes data outside the EEA, we implement appropriate safeguards (e.g., Standard Contractual Clauses).</p>

    <h2>6) Retention periods</h2>
    <ul>
      <li>Orders/invoices/accounting: 7 to 10 years (legal requirements).</li>
      <li>Customer account: while active, then deletion/archiving after 3 years of inactivity.</li>
      <li>Support requests: for the time needed, then limited archiving if needed.</li>
      <li>Security logs: 12 months.</li>
      <li>Marketing: until you withdraw consent or 3 years after last contact.</li>
    </ul>

    <h2>7) Cookies</h2>
    <p>We use strictly necessary cookies (cart, session, security) and analytics/marketing cookies only with your consent.</p>

    <h2>8) Security</h2>
    <p>We use appropriate technical and organizational measures to protect your data.</p>

    <h2>9) Your rights</h2>
    <p>You may have the right to access, rectify, erase, restrict processing, object, and request data portability.</p>
    <p>To exercise your rights: <a href="mailto:admin@shop-ebb.com">admin@shop-ebb.com</a></p>
    <p>You may also lodge a complaint with the Belgian Data Protection Authority: <a href="https://www.dataprotectionauthority.be" target="_blank" rel="noopener noreferrer">www.dataprotectionauthority.be</a></p>
  </div>
);

const PrivacyNL = () => (
  <div>
    <h2>1) Verwerkingsverantwoordelijke</h2>
    <p>De verwerkingsverantwoordelijke is EBB‑Shop, Rue du Centre 182, 5030 Gembloux, btw BE0700 100 114.</p>

    <h2>2) Welke gegevens verwerken wij?</h2>
    <p>Afhankelijk van uw gebruik verwerken wij o.a.:</p>
    <ul>
      <li><strong>Identificatie- en contactgegevens:</strong> naam, voornaam, bedrijf (indien van toepassing), btw‑nummer, e‑mail, telefoon, factuur- en leveradres.</li>
      <li><strong>Bestelgegevens:</strong> artikelen, aantallen, prijzen, kortingen, aankoopgeschiedenis, retouren, omruilingen, klantendienst.</li>
      <li><strong>Betalingsgegevens:</strong> betaalstatus, transactie‑referenties/IDs (wij bewaren geen volledige kaartgegevens).</li>
      <li><strong>Technische gegevens:</strong> IP‑adres, identifiers/tijdstempels, beveiligingslogs, surfgegevens en cookies.</li>
    </ul>

    <h2>3) Doeleinden en rechtsgrond</h2>
    <ul>
      <li>Aanmaken/beheren van een account: uitvoering van een overeenkomst / precontractuele stappen.</li>
      <li>Verwerken en uitvoeren van bestellingen: uitvoering van een overeenkomst.</li>
      <li>Facturatie en boekhouding: wettelijke verplichting.</li>
      <li>Fraudepreventie en beveiliging: gerechtvaardigd belang.</li>
      <li>Beantwoorden van vragen: gerechtvaardigd belang en/of uitvoering van een overeenkomst.</li>
      <li>Marketing: toestemming wanneer vereist; uitschrijven kan altijd.</li>
    </ul>

    <h2>4) Ontvangers – verwerkers</h2>
    <ul>
      <li><strong>OVH</strong> (VPS hosting) voor hosting en technische werking.</li>
      <li><strong>Mollie</strong> (betalingen) voor betalingsverwerking en transactie‑statussen.</li>
      <li><strong>bpost</strong> (transport/levering) voor verzending, tracking en retouren.</li>
      <li>Andere technische dienstverleners en professionele adviseurs indien nodig.</li>
      <li>Overheden indien wettelijk vereist.</li>
    </ul>

    <h2>5) Doorgifte buiten de EER</h2>
    <p>Indien een dienstverlener gegevens buiten de EER verwerkt, voorzien wij passende waarborgen.</p>

    <h2>6) Bewaartermijnen</h2>
    <ul>
      <li>Bestellingen/facturen/boekhouding: 7 tot 10 jaar (wettelijke verplichtingen).</li>
      <li>Klantaccount: zolang actief, daarna verwijdering/archivering na 3 jaar inactiviteit.</li>
      <li>Klantendienst: zolang nodig, daarna beperkte archivering indien nodig.</li>
      <li>Beveiligingslogs: 12 maanden.</li>
      <li>Marketing: tot u uw toestemming intrekt of 3 jaar na laatste contact.</li>
    </ul>

    <h2>7) Cookies</h2>
    <p>Wij gebruiken strikt noodzakelijke cookies en analytische/marketingcookies alleen met uw toestemming.</p>

    <h2>8) Beveiliging</h2>
    <p>Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen.</p>

    <h2>9) Uw rechten</h2>
    <p>U heeft recht op inzage, rectificatie, wissing, beperking, bezwaar en overdraagbaarheid.</p>
    <p>Uitoefenen van rechten: <a href="mailto:admin@shop-ebb.com">admin@shop-ebb.com</a></p>
    <p>U kan ook een klacht indienen bij de Belgische Gegevensbeschermingsautoriteit: <a href="https://www.gegevensbeschermingsautoriteit.be" target="_blank" rel="noopener noreferrer">www.gegevensbeschermingsautoriteit.be</a></p>
  </div>
);

// ============== TERMS AND CONDITIONS ==============

const TermsFR = () => (
  <div>
    <h2>Article 1 : Champ d'application</h2>
    <p>Les présentes CGV s'appliquent à toutes les commandes passées sur le site EBB‑Shop. En passant commande, le Client reconnaît avoir pris connaissance des présentes CGV et les accepter sans réserve.</p>

    <h2>Article 2 : Produits et caractéristiques</h2>
    <p>Les produits sont décrits et présentés sur le site avec la plus grande exactitude possible. Les photographies et descriptions sont aussi fidèles que possible, mais ne constituent pas un engagement contractuel.</p>

    <h2>Article 3 : Prix</h2>
    <p>Les prix sont indiqués en euros (EUR), TVA comprise (taux applicable en Belgique) et hors frais de livraison, sauf mention contraire. Le Vendeur se réserve le droit de modifier ses prix à tout moment.</p>

    <h2>Article 4 : Commande</h2>
    <ol>
      <li>Le Client sélectionne les produits et les ajoute à son panier.</li>
      <li>Avant validation, le Client vérifie le récapitulatif de sa commande.</li>
      <li>Le Client valide sa commande et procède au paiement.</li>
      <li>Une confirmation de commande est envoyée par e‑mail après validation du paiement.</li>
    </ol>

    <h2>Article 5 : Paiement</h2>
    <p>Le paiement s'effectue en ligne via la plateforme sécurisée Mollie. Les moyens de paiement acceptés sont : carte bancaire, Bancontact, virement bancaire.</p>

    <h2>Article 6 : Livraison</h2>
    <p>Les produits sont livrés à l'adresse indiquée par le Client lors de la commande. Le transport est assuré par bpost. Les délais indicatifs sont communiqués lors de la commande.</p>

    <h2>Article 7 : Droit de rétractation (B2C)</h2>
    <p>Conformément aux articles VI.47 à VI.53 du Code de droit économique, le consommateur bénéficie d'un délai de <strong>14 jours calendrier</strong> pour exercer son droit de rétractation, sans avoir à justifier de motif.</p>

    <h2>Article 8 : Garanties légales (B2C)</h2>
    <p><strong>Garantie de conformité :</strong> Le consommateur dispose d'un délai de 2 ans à compter de la livraison pour invoquer un défaut de conformité.</p>
    <p><strong>Garantie contre les vices cachés :</strong> Le Client peut également invoquer la garantie contre les vices cachés (articles 1641 et suivants du Code civil).</p>

    <h2>Article 9 : Garanties B2B</h2>
    <p>Pour les ventes entre professionnels, les garanties légales B2C peuvent ne pas s'appliquer. Toute réclamation doit être formulée par écrit dans un délai raisonnable (8 jours recommandés) après découverte du défaut.</p>

    <h2>Article 10 : Responsabilité</h2>
    <p>Le Vendeur ne saurait être tenu responsable en cas de force majeure, d'interruption technique, ou de difficultés liées au transporteur. La responsabilité du Vendeur est limitée au montant payé par le Client pour le produit concerné.</p>

    <h2>Article 11 : Propriété intellectuelle</h2>
    <p>Tous les éléments du site sont la propriété exclusive du Vendeur ou de ses partenaires. Toute reproduction sans autorisation préalable est interdite.</p>

    <h2>Article 12 : Protection des données personnelles</h2>
    <p>Le traitement des données personnelles est régi par notre Politique de confidentialité, conforme au RGPD et à la loi belge du 30 juillet 2018.</p>

    <h2>Article 13 : Réclamations et litiges</h2>
    <p><strong>Médiation :</strong> Le consommateur peut recourir gratuitement à un service de médiation agréé. Liste disponible sur : <a href="https://www.belmed.be" target="_blank" rel="noopener noreferrer">www.belmed.be</a></p>
    <p><strong>Juridiction compétente :</strong> Tout litige relève de la compétence exclusive des tribunaux de l'arrondissement de Namur (Belgique).</p>
    <p><strong>Droit applicable :</strong> Les présentes CGV sont régies par le droit belge.</p>
  </div>
);

const TermsEN = () => (
  <div>
    <h2>Article 1: Scope</h2>
    <p>These GTC apply to all orders placed on the EBB‑Shop website. By placing an order, the Customer acknowledges having read and accepted these GTC without reservation.</p>

    <h2>Article 2: Products and characteristics</h2>
    <p>Products are described and presented on the website as accurately as possible. Photographs and descriptions do not constitute a contractual commitment.</p>

    <h2>Article 3: Prices</h2>
    <p>Prices are stated in euros (EUR), VAT included (applicable Belgian rate) and excluding delivery costs, unless otherwise stated. The Seller reserves the right to modify prices at any time.</p>

    <h2>Article 4: Order</h2>
    <ol>
      <li>The Customer selects products and adds them to the cart.</li>
      <li>Before confirmation, the Customer verifies the order summary.</li>
      <li>The Customer confirms the order and proceeds to payment.</li>
      <li>An order confirmation is sent by email after payment validation.</li>
    </ol>

    <h2>Article 5: Payment</h2>
    <p>Payment is made online via the secure Mollie platform. Accepted payment methods include: credit card, Bancontact, bank transfer.</p>

    <h2>Article 6: Delivery</h2>
    <p>Products are delivered to the address provided by the Customer. Transport is provided by bpost. Indicative delivery times are communicated during the order.</p>

    <h2>Article 7: Right of withdrawal (B2C)</h2>
    <p>Under articles VI.47 to VI.53 of the Economic Code, consumers have a <strong>14 calendar day</strong> period to exercise their right of withdrawal without justification.</p>

    <h2>Article 8: Legal guarantees (B2C)</h2>
    <p><strong>Conformity guarantee:</strong> Consumers have 2 years from delivery to invoke non-conformity.</p>
    <p><strong>Hidden defect guarantee:</strong> Customers may also invoke the guarantee against hidden defects (articles 1641 et seq. of the Civil Code).</p>

    <h2>Article 9: B2B guarantees</h2>
    <p>For B2B sales, B2C legal guarantees may not apply. Any claim must be made in writing within a reasonable time (8 days recommended) after defect discovery.</p>

    <h2>Article 10: Liability</h2>
    <p>The Seller cannot be held liable in case of force majeure, technical interruption, or carrier difficulties. Liability is limited to the amount paid for the product concerned.</p>

    <h2>Article 11: Intellectual property</h2>
    <p>All website elements are the exclusive property of the Seller or its partners. Any reproduction without prior authorization is prohibited.</p>

    <h2>Article 12: Personal data protection</h2>
    <p>Personal data processing is governed by our Privacy Policy, compliant with GDPR and Belgian law of July 30, 2018.</p>

    <h2>Article 13: Claims and disputes</h2>
    <p><strong>Mediation:</strong> Consumers can use a free approved mediation service. List available at: <a href="https://www.belmed.be" target="_blank" rel="noopener noreferrer">www.belmed.be</a></p>
    <p><strong>Competent jurisdiction:</strong> Any dispute falls under the exclusive jurisdiction of the courts of the Namur district (Belgium).</p>
    <p><strong>Applicable law:</strong> These GTC are governed by Belgian law.</p>
  </div>
);

const TermsNL = () => (
  <div>
    <h2>Artikel 1: Toepassingsgebied</h2>
    <p>Deze AVV zijn van toepassing op alle bestellingen op de EBB‑Shop website. Door een bestelling te plaatsen, erkent de Klant deze AVV te hebben gelezen en zonder voorbehoud te aanvaarden.</p>

    <h2>Artikel 2: Producten en kenmerken</h2>
    <p>Producten worden zo nauwkeurig mogelijk beschreven en gepresenteerd op de website. Foto's en beschrijvingen vormen geen contractuele verplichting.</p>

    <h2>Artikel 3: Prijzen</h2>
    <p>Prijzen worden vermeld in euro's (EUR), btw inbegrepen (toepasselijk Belgisch tarief) en exclusief leveringskosten, tenzij anders vermeld.</p>

    <h2>Artikel 4: Bestelling</h2>
    <ol>
      <li>De Klant selecteert producten en voegt ze toe aan het winkelmandje.</li>
      <li>Vóór bevestiging controleert de Klant de bestelsamenvatting.</li>
      <li>De Klant bevestigt de bestelling en gaat over tot betaling.</li>
      <li>Een bestelbevestiging wordt per e-mail verzonden na validatie van de betaling.</li>
    </ol>

    <h2>Artikel 5: Betaling</h2>
    <p>Betaling vindt online plaats via het beveiligde Mollie platform. Geaccepteerde betaalmethoden zijn: kredietkaart, Bancontact, bankoverschrijving.</p>

    <h2>Artikel 6: Levering</h2>
    <p>Producten worden geleverd op het door de Klant opgegeven adres. Transport wordt verzorgd door bpost. Indicatieve levertijden worden medegedeeld tijdens de bestelling.</p>

    <h2>Artikel 7: Herroepingsrecht (B2C)</h2>
    <p>Volgens artikelen VI.47 tot VI.53 van het Wetboek van economisch recht hebben consumenten een herroepingstermijn van <strong>14 kalenderdagen</strong> zonder opgave van reden.</p>

    <h2>Artikel 8: Wettelijke garanties (B2C)</h2>
    <p><strong>Conformiteitsgarantie:</strong> Consumenten hebben 2 jaar vanaf levering om non-conformiteit in te roepen.</p>
    <p><strong>Garantie tegen verborgen gebreken:</strong> Klanten kunnen ook de garantie tegen verborgen gebreken inroepen (artikelen 1641 e.v. van het Burgerlijk Wetboek).</p>

    <h2>Artikel 9: B2B garanties</h2>
    <p>Voor B2B verkoop zijn B2C wettelijke garanties mogelijk niet van toepassing. Elke klacht moet schriftelijk worden ingediend binnen een redelijke termijn (8 dagen aanbevolen).</p>

    <h2>Artikel 10: Aansprakelijkheid</h2>
    <p>De Verkoper kan niet aansprakelijk worden gesteld bij overmacht, technische onderbreking of moeilijkheden met de vervoerder. Aansprakelijkheid is beperkt tot het betaalde bedrag.</p>

    <h2>Artikel 11: Intellectueel eigendom</h2>
    <p>Alle website-elementen zijn het exclusieve eigendom van de Verkoper of zijn partners. Elke reproductie zonder voorafgaande toestemming is verboden.</p>

    <h2>Artikel 12: Bescherming van persoonsgegevens</h2>
    <p>Verwerking van persoonsgegevens wordt beheerst door ons Privacybeleid, in overeenstemming met de AVG en de Belgische wet van 30 juli 2018.</p>

    <h2>Artikel 13: Klachten en geschillen</h2>
    <p><strong>Bemiddeling:</strong> Consumenten kunnen gratis gebruik maken van een erkende bemiddelingsdienst. Lijst beschikbaar op: <a href="https://www.belmed.be" target="_blank" rel="noopener noreferrer">www.belmed.be</a></p>
    <p><strong>Bevoegde rechtbank:</strong> Elk geschil valt onder de exclusieve bevoegdheid van de rechtbanken van het arrondissement Namen (België).</p>
    <p><strong>Toepasselijk recht:</strong> Deze AVV worden beheerst door Belgisch recht.</p>
  </div>
);

// ============== RETURN POLICY ==============

const ReturnsFR = () => (
  <div>
    <h2>1) Champ d'application</h2>
    <p>La présente politique s'applique aux achats effectués sur le site EBB‑Shop. Elle distingue :</p>
    <ul>
      <li>les retours liés au droit de rétractation (consommateurs – B2C) ;</li>
      <li>les retours liés à un article défectueux ou non conforme (B2C et B2B) ;</li>
      <li>les retours "commerciaux" (ex. échange volontaire) lorsqu'ils sont proposés.</li>
    </ul>

    <h2>2) Droit de rétractation (B2C)</h2>
    <p>Vous disposez d'un délai de <strong>14 jours calendrier</strong> pour vous rétracter sans devoir motiver votre décision.</p>
    <p>Le délai court à partir du lendemain de la réception du produit.</p>
    <h3>Comment exercer la rétractation</h3>
    <ol>
      <li>Informez-nous de votre décision avant l'expiration du délai de 14 jours (e-mail à admin@shop-ebb.com).</li>
      <li>Renvoyez ensuite le(s) produit(s) au plus tard dans les 14 jours suivant votre notification.</li>
    </ol>
    <h3>Remboursement</h3>
    <ul>
      <li>Le remboursement porte sur le prix payé pour le(s) produit(s) et les frais de livraison standards initiaux.</li>
      <li>Le remboursement est effectué via le même moyen de paiement dans un délai de 14 jours.</li>
    </ul>

    <h2>3) Frais et modalités de renvoi</h2>
    <p>Sauf mention contraire, les <strong>frais de retour sont à charge du client</strong> en cas de rétractation.</p>
    <p><strong>Adresse de retour :</strong><br />
    EBB‑Shop – Service Retours<br />
    Rue du Centre 182<br />
    5030 Gembloux<br />
    Belgique</p>

    <h2>4) État des produits</h2>
    <ul>
      <li>Le produit doit être renvoyé dans un état permettant sa remise en vente.</li>
      <li>Avec ses accessoires, notices et emballages d'origine lorsque raisonnablement possible.</li>
    </ul>

    <h2>5) Exceptions au droit de rétractation</h2>
    <ul>
      <li>Produits confectionnés selon les spécifications du client ou personnalisés</li>
      <li>Biens scellés ne pouvant être renvoyés pour des raisons d'hygiène, si descellés</li>
      <li>Produits périssables ou susceptibles de se détériorer rapidement</li>
    </ul>

    <h2>6) Produits défectueux ou non conformes</h2>
    <p>Si un produit est défectueux, endommagé à la réception ou non conforme :</p>
    <ol>
      <li>Contactez-nous dans les meilleurs délais à admin@shop-ebb.com avec votre numéro de commande.</li>
      <li>Fournissez des photos ou vidéos du défaut si possible.</li>
      <li>Nous proposerons : remplacement, réparation, remboursement, ou réduction de prix.</li>
    </ol>
    <p>Si le défaut est avéré, <strong>EBB‑Shop prendra en charge les frais de retour</strong>.</p>

    <h2>7) Échanges (B2C)</h2>
    <p>Le Client peut demander un échange (taille, couleur) dans les 14 jours calendrier suivant la réception, sous réserve de disponibilité en stock.</p>

    <h2>8) Délais de traitement</h2>
    <p>Après réception et contrôle du retour, le traitement est généralement effectué sous <strong>7 à 14 jours ouvrables</strong>.</p>
  </div>
);

const ReturnsEN = () => (
  <div>
    <h2>1) Scope</h2>
    <p>This policy applies to purchases made on the EBB‑Shop website. It distinguishes:</p>
    <ul>
      <li>returns related to the right of withdrawal (consumers – B2C);</li>
      <li>returns related to a defective or non-compliant item (B2C and B2B);</li>
      <li>"commercial" returns (e.g., voluntary exchange) when offered.</li>
    </ul>

    <h2>2) Right of withdrawal (B2C)</h2>
    <p>You have a <strong>14 calendar day</strong> period to withdraw without justification.</p>
    <p>The period runs from the day after receipt of the product.</p>
    <h3>How to exercise withdrawal</h3>
    <ol>
      <li>Inform us of your decision before the 14-day deadline expires (email to admin@shop-ebb.com).</li>
      <li>Return the product(s) no later than 14 days after your notification.</li>
    </ol>
    <h3>Refund</h3>
    <ul>
      <li>The refund covers the price paid for the product(s) and standard initial delivery costs.</li>
      <li>The refund is made via the same payment method within 14 days.</li>
    </ul>

    <h2>3) Return costs and procedures</h2>
    <p>Unless otherwise stated, <strong>return costs are borne by the customer</strong> in case of withdrawal.</p>
    <p><strong>Return address:</strong><br />
    EBB‑Shop – Returns Department<br />
    Rue du Centre 182<br />
    5030 Gembloux<br />
    Belgium</p>

    <h2>4) Product condition</h2>
    <ul>
      <li>The product must be returned in a condition allowing resale.</li>
      <li>With its accessories, manuals, and original packaging when reasonably possible.</li>
    </ul>

    <h2>5) Exceptions to the right of withdrawal</h2>
    <ul>
      <li>Products made to customer specifications or personalized</li>
      <li>Sealed goods that cannot be returned for hygiene reasons, if unsealed</li>
      <li>Perishable products or products likely to deteriorate rapidly</li>
    </ul>

    <h2>6) Defective or non-compliant products</h2>
    <p>If a product is defective, damaged on receipt, or does not conform:</p>
    <ol>
      <li>Contact us as soon as possible at admin@shop-ebb.com with your order number.</li>
      <li>Provide photos or videos of the defect if possible.</li>
      <li>We will offer: replacement, repair, refund, or price reduction.</li>
    </ol>
    <p>If the defect is confirmed, <strong>EBB‑Shop will cover return costs</strong>.</p>

    <h2>7) Exchanges (B2C)</h2>
    <p>The Customer may request an exchange (size, color) within 14 calendar days from receipt, subject to stock availability.</p>

    <h2>8) Processing times</h2>
    <p>After receipt and inspection, processing is generally completed within <strong>7 to 14 business days</strong>.</p>
  </div>
);

const ReturnsNL = () => (
  <div>
    <h2>1) Toepassingsgebied</h2>
    <p>Dit beleid is van toepassing op aankopen op de EBB‑Shop website. Het onderscheidt:</p>
    <ul>
      <li>retouren in verband met het herroepingsrecht (consumenten – B2C);</li>
      <li>retouren in verband met een defect of niet-conform artikel (B2C en B2B);</li>
      <li>"commerciële" retouren (bijv. vrijwillige ruil) wanneer aangeboden.</li>
    </ul>

    <h2>2) Herroepingsrecht (B2C)</h2>
    <p>U heeft een termijn van <strong>14 kalenderdagen</strong> om zonder opgave van reden te herroepen.</p>
    <p>De termijn loopt vanaf de dag na ontvangst van het product.</p>
    <h3>Hoe het herroepingsrecht uitoefenen</h3>
    <ol>
      <li>Informeer ons over uw beslissing vóór het verstrijken van de termijn van 14 dagen (e-mail naar admin@shop-ebb.com).</li>
      <li>Stuur vervolgens het/de product(en) terug uiterlijk binnen 14 dagen na uw kennisgeving.</li>
    </ol>
    <h3>Terugbetaling</h3>
    <ul>
      <li>De terugbetaling omvat de prijs betaald voor het/de product(en) en de initiële standaard leveringskosten.</li>
      <li>De terugbetaling wordt gedaan via dezelfde betaalmethode binnen 14 dagen.</li>
    </ul>

    <h2>3) Retourkosten en procedures</h2>
    <p>Tenzij anders vermeld, zijn <strong>retourkosten voor rekening van de klant</strong> bij herroeping.</p>
    <p><strong>Retouradres:</strong><br />
    EBB‑Shop – Afdeling Retouren<br />
    Rue du Centre 182<br />
    5030 Gembloux<br />
    België</p>

    <h2>4) Staat van producten</h2>
    <ul>
      <li>Het product moet worden teruggestuurd in een staat die wederverkoop mogelijk maakt.</li>
      <li>Met accessoires, handleidingen en originele verpakking wanneer redelijkerwijs mogelijk.</li>
    </ul>

    <h2>5) Uitzonderingen op het herroepingsrecht</h2>
    <ul>
      <li>Producten vervaardigd volgens klantspecificaties of gepersonaliseerd</li>
      <li>Verzegelde goederen die om hygiënische redenen niet kunnen worden teruggestuurd, indien ontzegeld</li>
      <li>Bederfelijke producten of producten die snel kunnen verslechteren</li>
    </ul>

    <h2>6) Defecte of niet-conforme producten</h2>
    <p>Als een product defect, beschadigd bij ontvangst of niet conform de bestelling is:</p>
    <ol>
      <li>Neem zo snel mogelijk contact met ons op via admin@shop-ebb.com met uw bestelnummer.</li>
      <li>Verstrek indien mogelijk foto's of video's van het defect.</li>
      <li>We zullen aanbieden: vervanging, reparatie, terugbetaling of prijsvermindering.</li>
    </ol>
    <p>Als het defect wordt bevestigd, <strong>zal EBB‑Shop de retourkosten dekken</strong>.</p>

    <h2>7) Omruilingen (B2C)</h2>
    <p>De Klant kan een omruiling (maat, kleur) aanvragen binnen 14 kalenderdagen na ontvangst, mits beschikbaarheid in voorraad.</p>

    <h2>8) Verwerkingstijden</h2>
    <p>Na ontvangst en inspectie wordt de verwerking over het algemeen voltooid binnen <strong>7 tot 14 werkdagen</strong>.</p>
  </div>
);

export default LegalPage;
